import React, {useState} from 'react'
import {ContactGroupMap} from '../../../../../services/cellcast.v2.types'
import './ContactMobileCard.scss'
import { formatDateWithUserTimezone } from '../../wallet-card-templates/utils'
import { isNumber } from '../../../../Utils/_helpers/number'
import { isVirtualNumber } from '../../../../Utils/_helpers/number'
import { Tag } from '../../../../../services/cellcast.type'
import { getValueByFields } from '../../../../Utils/getValueByFields'

interface ContactMobileAction {
  key: string
  label: string
  icon?: string
  danger?: boolean
  onClick: () => void
}

interface ContactMobileCardProps {
  contact: ContactGroupMap
  isSelected: boolean
  onSelect: (id: string) => void
  onDelete: () => void
  onSubscribe: () => void
  onUnsubscribe: () => void
  actions?: ContactMobileAction[]
  showTag?: boolean
  showSubscribeStatus?: boolean
  groupFields?: string[]
}

export const ContactMobileCard: React.FC<ContactMobileCardProps> = ({
  contact,
  isSelected,
  onSelect,
  onDelete,
  onSubscribe,
  onUnsubscribe,
  actions = [],
  showTag = true,
  showSubscribeStatus = true,
  groupFields = [],
}) => {
  const isUserBlock = Boolean(contact.isBlockedByUser || contact.isBlocked || contact.is_blocked)
  const [showActions, setShowActions] = useState(false)

  const handleActionClick = () => {
    setShowActions(!showActions)
  }

  const firstName = getValueByFields(contact, 'First Name')
  const lastName = getValueByFields(contact, 'Last Name')
  const email = getValueByFields(contact, 'Email') || 'N/A'
  const phoneNumber = isVirtualNumber(contact.full_number || contact.number)
    ? '-'
    : (contact.full_number || contact.number)

  // Get display name (first name + last name, or phone number as fallback)
  const displayName = firstName || lastName
    ? `${firstName} ${lastName}`.trim()
    : phoneNumber

  return (
    <div className={`contact-mobile-card ${isSelected ? 'selected' : ''}`}>
      {/* Header with checkbox and name */}
      <div className='card-header-section'>
        <div className='d-flex align-items-center gap-3 flex-grow-1'>
          <input
            type='checkbox'
            className='form-check-input contact-checkbox'
            checked={isSelected}
            onChange={() => onSelect(contact._id)}
            aria-label={`Select ${displayName}`}
          />
          <div className='contact-info-section'>
            <h5 className='contact-phone'>{contact.full_number || contact.number}</h5>
            {contact.creatorName && (
              <span className='contact-creator text-muted'>Created by: {contact.creatorName}</span>
            )}
            <h5 className='contact-name'>{displayName}</h5>
          </div>
        </div>
        <button
          className='btn btn-action-mobile'
          onClick={handleActionClick}
          aria-label='Actions menu'
          aria-expanded={showActions}
        >
          <i className='fas fa-ellipsis-v'></i>
        </button>
      </div>

      {/* Actions dropdown */}
      {showActions && (
        <div className='actions-dropdown'>
          {showSubscribeStatus && (
            <button
              className='action-item'
              onClick={() => {
                if (isUserBlock) {
                  onSubscribe()
                } else {
                  onUnsubscribe()
                }
                setShowActions(false)
              }}
            >
              <i
                className={`fas fa-${
                  isUserBlock
                    ? 'user-plus'
                    : 'user-minus'
                } me-2`}
              ></i>
              {isUserBlock
                ? 'Subscribe'
                : 'Unsubscribe'}
            </button>
          )}
          {actions.map((a) => (
            <button
              key={a.key}
              className={`action-item ${a.danger ? 'text-danger' : ''}`}
              onClick={() => {
                a.onClick()
                setShowActions(false)
              }}
            >
              {a.icon && <i className={`${a.icon} me-2`}></i>}
              {a.label}
            </button>
          ))}
          <button
            className='action-item text-danger'
            onClick={() => {
              onDelete()
              setShowActions(false)
            }}
          >
            <i className='fas fa-trash me-2'></i>
            Delete
          </button>
        </div>
      )}

      {/* Contact details grid */}
      <div className='card-details-grid'>
        {/* First Name */}
        {firstName && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-user me-1'></i>
              First Name
            </span>
            <span className='detail-value'>{firstName}</span>
          </div>
        )}

        {/* Last Name */}
        {lastName && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-user me-1'></i>
              Last Name
            </span>
            <span className='detail-value'>{lastName}</span>
          </div>
        )}

        {/* Email */}
        {email && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-envelope me-1'></i>
              Email
            </span>
            <span className='detail-value'>{email}</span>
          </div>
        )}

        {/* Phone Number */}
        {phoneNumber && phoneNumber !== '-' && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-phone me-1'></i>
              Phone
            </span>
            <span className='detail-value'>{phoneNumber}</span>
          </div>
        )}

        {/* Tags and Lists */}
        {showTag && contact.tags && contact.tags.length > 0 && (
          <div className='detail-item full-width'>
            <div className='detail-value'>
              <i className='fas fa-tags me-1'></i>
              <div className='d-flex gap-2 align-items-center flex-wrap'>
                {contact.tags
                  .map((data: Tag, i: number) => (
                    <div
                      key={i}
                      className='inbox-tags'
                      style={{
                        color: (data?.color) ? 'white' : 'black',
                        backgroundColor: (data?.color || ''),
                      }}
                    >
                      {data?.name}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
